import {
    getCategorizedRulesAndTotals,
    isMoneyMapIncomeSplitEligible,
    MoneyMapFilterState,
    MoneyMapVisual,
} from '@fruitful/money-map';
import type {
    MoneyMapAccountWithAccountDetails,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useAtomValue } from 'jotai';
import React, { useCallback, useMemo, useState } from 'react';
import { type LayoutChangeEvent } from 'react-native';
import { H6Emphasized, IconButton, XIcon, XStack, YStack } from 'smoothie';

import { selectedMembershipAtom } from '../../entities/entityAtoms';
import { getMoneyMapperEffectiveMonthlyIncomeCents } from '../utils/moneyMapperEffectiveIncome';

/** Expanded ALL view: income + connector + curve start + gap before bars (see MoneyMapVisual). */
const MONEY_MAP_VISUAL_VERTICAL_OVERHEAD = 230;

interface MoneyMapperDiagramPanelProps {
    moneyMap: MoneyMapWithRulesAndAccounts;
    accounts: MoneyMapAccountWithAccountDetails[];
    onPressClose: () => void;
}

export const MoneyMapperDiagramPanel = ({
    moneyMap,
    accounts,
    onPressClose,
}: MoneyMapperDiagramPanelProps) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const [filter] = useState<MoneyMapFilterState>(MoneyMapFilterState.ALL);
    const [visualSlotHeight, setVisualSlotHeight] = useState(0);
    const onVisualSlotLayout = useCallback((event: LayoutChangeEvent) => {
        setVisualSlotHeight(event.nativeEvent.layout.height);
    }, []);

    const { maxExpandedBarHeight, maxCollapsedBarHeight } = useMemo(() => {
        if (visualSlotHeight <= 0) {
            return {
                maxExpandedBarHeight: 384,
                maxCollapsedBarHeight: 240,
            };
        }
        const expanded = Math.max(
            168,
            Math.floor(visualSlotHeight - MONEY_MAP_VISUAL_VERTICAL_OVERHEAD),
        );
        return {
            maxExpandedBarHeight: expanded,
            maxCollapsedBarHeight: Math.max(120, Math.floor(expanded * 0.58)),
        };
    }, [visualSlotHeight]);

    const effectiveIncomeCents = getMoneyMapperEffectiveMonthlyIncomeCents({
        moneyMap,
        membershipIncomes: membership?.membershipIncomes,
    });

    const { billsTotalCents, goalsTotalCents, spendTotalCents } =
        useMemo(() => {
            return getCategorizedRulesAndTotals(moneyMap, accounts);
        }, [accounts, moneyMap]);

    const isIncomeSplitEligible = isMoneyMapIncomeSplitEligible(
        moneyMap,
        accounts,
    );

    const closeButton = (
        <IconButton
            variant="secondary"
            size="tiny"
            icon={XIcon}
            onPress={onPressClose}
        />
    );

    return (
        <YStack
            flex={1}
            minHeight={0}
            width="100%"
            backgroundColor="$white"
            paddingHorizontal={32}
            paddingTop={20}
            paddingBottom={24}
            gap={24}
        >
            <XStack
                width="100%"
                alignItems="center"
                justifyContent="space-between"
            >
                <H6Emphasized color="$gray1200">Money Map</H6Emphasized>
                {closeButton}
            </XStack>
            <YStack
                flex={1}
                minHeight={0}
                width="100%"
                justifyContent="center"
                onLayout={onVisualSlotLayout}
            >
                <MoneyMapVisual
                    incomeTotalCents={effectiveIncomeCents}
                    billsTotalCents={billsTotalCents ?? 0}
                    spendTotalCents={spendTotalCents ?? 0}
                    goalsTotalCents={goalsTotalCents ?? 0}
                    filter={filter}
                    setFilter={() => {}}
                    isIncomeSplitEligible={isIncomeSplitEligible}
                    visualSizing={{
                        maxExpandedBarHeight,
                        maxCollapsedBarHeight,
                    }}
                />
            </YStack>
        </YStack>
    );
};
